import { Client } from "@xmpp/client"
import { useCallback, useState } from "react"
import {
  DeliveryOrder,
  DocumentDriverContext,
  DocumentLink,
  ExtractedMetadata,
  Fasten,
  Marker,
  Message,
  MessageFileShared,
  MessageRetraction,
  processArchivedBatch,
  Reaction,
} from "../reducers/messagesSlice"
import { Chat } from "../reducers/chatsSlice"
import { AppDispatch } from "../reducers/store"
import { iterateThroughArchivePages, MAMQueryOptions } from "../api/xeps/MAM"
import MAMStanzaProcessor from "../api/xeps/MAMStanzaProcessor"
import { captureError } from "../ErrorHandlers"

const useInfiniteScroll = (): [
  boolean,
  (
    chat: Chat,
    messages: Message[],
    client: Client,
    myJid: string,
    dispatch: AppDispatch,
  ) => Promise<boolean | undefined>,
] => {
  const [isFetchingPage, setIsFetching] = useState(false)

  const fetchMoreMessages = useCallback(
    async (
      chat: Chat,
      messages: Message[],
      client: Client,
      myJid: string,
      dispatch: AppDispatch,
    ) => {
      if (messages.length === 0) return

      setIsFetching(true)

      const anchor = { chatJid: chat.jid, messageId: messages[0].id }
      let options: MAMQueryOptions = {
        beforeId: anchor.messageId,
      }

      if (chat.room) {
        options = {
          ...options,
          toJid: chat.jid,
          flipPage: true,
        }
      } else {
        options = {
          ...options,
          withJid: chat.jid,
          flipPage: true,
        }
      }

      const markersAccumulator: Marker[][] = []
      const messagesAccumulator: Message[][] = []
      const reactionsAccumulator: Reaction[][] = []
      const messageFileSharesAccumulator: MessageFileShared[][] = []
      const messageRetractionsAccumulator: MessageRetraction[][] = []
      const extractedMetadataAccumulator: ExtractedMetadata[][] = []
      const documentDriverContextAccumulator: DocumentDriverContext[][] = []
      const deliveryOrdersAccumulator: DeliveryOrder[][] = []
      const documentLinksAccumulator: DocumentLink[][] = []
      const routeUpdatesAccumulator: Fasten[][] = []
      try {
        const fetchResult = await iterateThroughArchivePages(
          client,
          options,
          MAMStanzaProcessor.accumulatePages(
            messagesAccumulator,
            markersAccumulator,
            reactionsAccumulator,
            messageFileSharesAccumulator,
            messageRetractionsAccumulator,
            extractedMetadataAccumulator,
            documentDriverContextAccumulator,
            deliveryOrdersAccumulator,
            documentLinksAccumulator,
            routeUpdatesAccumulator,
            client,
          ),
          "desc",
          25,
        )

        // Process the accumulated data and dispatch a single action
        const accumulatedData = {
          messagesAccumulator,
          markersAccumulator,
          reactionsAccumulator,
          messageFileSharesAccumulator,
          messageRetractionsAccumulator,
          extractedMetadataAccumulator,
          documentDriverContextAccumulator,
          deliveryOrdersAccumulator,
          documentLinksAccumulator,
          routeUpdatesAccumulator,
        }

        const payload = MAMStanzaProcessor.processAccumulatedData(
          accumulatedData,
          "desc",
          !!options.flipPage,
          client.jid!.toString(),
          "prepend",
        )

        dispatch(processArchivedBatch({ ...payload, anchor }))

        return fetchResult.hasMore
      } catch (error) {
        captureError(error, {
          origin: "UserAction",
          extra: { message: "infiniteScroll" },
        })
      } finally {
        setIsFetching(false)
      }
    },
    [],
  )

  return [isFetchingPage, fetchMoreMessages]
}

export default useInfiniteScroll
